import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronRight, Gift, Calendar, Check, Clock, MapPin, Users, Trophy, ArrowDown, Star, Award, Globe, Map, DollarSign, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import GiveawayTermsModal from './GiveawayTermsModal';

const GiveawaySection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const { t } = useTranslation();

  // Set next winner announcement date (example: end of current month)
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const difference = nextMonth.getTime() - now.getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = {
        'form-name': 'giveaway-entry',
        email
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('giveaway-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Floating animation variants
  const floatingAnimation = {
    initial: { y: 0 },
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: 'reverse' as const
      }
    }
  };

  return (
    <section className="relative py-16 overflow-hidden bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute top-20 left-10 text-4xl opacity-20 dark:opacity-10"
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
        >
          🦁
        </motion.div>
        <motion.div 
          className="absolute top-40 right-20 text-4xl opacity-20 dark:opacity-10"
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
        >
          🦒
        </motion.div>
        <motion.div 
          className="absolute bottom-20 left-1/4 text-4xl opacity-20 dark:opacity-10"
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 2 }}
        >
          🦓
        </motion.div>
        <motion.div 
          className="absolute bottom-40 right-1/4 text-4xl opacity-20 dark:opacity-10"
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1.5 }}
        >
          🐘
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center bg-ta-green/10 rounded-full px-3 py-1 text-sm text-ta-green mb-4"
          >
            <Gift className="h-4 w-4 mr-2" />
            {t('giveaway.subtitle')}
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4"
          >
            🎉 {t('giveaway.title')}
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {t('giveaway.description')}
          </motion.p>
          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToForm}
            className="mt-6 inline-flex items-center px-6 py-3 bg-ta-green text-white rounded-full hover:bg-ta-green-dark transition-all shadow-md hover:shadow-lg"
          >
            🎉 {t('giveaway.enterButton')}
            <ArrowDown className="ml-2 h-4 w-4 animate-bounce" />
          </motion.button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                <Award className="h-3 w-3 mr-1" />
                Exclusive Safari Giveaway
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                Win a Free Safari Adventure –
                <span className="text-ta-green"> Explore Uganda!</span>
              </h2>
              
              <p className="text-gray-600 mb-6 text-sm md:text-base">
                Enter our exclusive giveaway for a chance to win a fully-funded 5-day safari adventure in Uganda's most spectacular national parks. Experience wildlife, landscapes, and culture like never before!
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Benefit 1 */}
                <div className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                    <Map className="h-4 w-4 text-ta-green" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-0.5">5-Day Safari</h3>
                    <p className="text-xs text-gray-500">Comprehensive wildlife experience</p>
                  </div>
                </div>

                {/* Benefit 2 */}
                <div className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                    <Users className="h-4 w-4 text-ta-green" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-0.5">Expert Guides</h3>
                    <p className="text-xs text-gray-500">Professional local guides</p>
                  </div>
                </div>

                {/* Benefit 3 */}
                <div className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                    <DollarSign className="h-4 w-4 text-ta-green" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-0.5">All-Inclusive</h3>
                    <p className="text-xs text-gray-500">Accommodation, meals & activities</p>
                  </div>
                </div>

                {/* Benefit 4 */}
                <div className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 bg-ta-green/20 rounded-lg flex items-center justify-center backdrop-blur-sm group-hover:bg-ta-green/30 transition-colors">
                    <Camera className="h-4 w-4 text-ta-green" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-0.5">Photo Package</h3>
                    <p className="text-xs text-gray-500">Professional photography included</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setIsTermsModalOpen(true)}
                className="group inline-flex items-center text-ta-green hover:underline"
              >
                View full terms & conditions
                <ChevronRight className="ml-1 h-4 w-4" />
              </button>
              
              <div className="flex items-center mt-4">
                <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="text-sm font-medium">19 winners to be selected</span>
              </div>
            </div>
          </motion.div>
          
          {/* Right Column - Form & Countdown */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {/* Countdown Timer */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-100 dark:border-gray-700 mb-6">
              <div className="flex items-center mb-4">
                <Clock className="h-5 w-5 text-ta-green mr-2" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('giveaway.nextWinner')}</h3>
              </div>
              
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-ta-green">{timeLeft.days}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('giveaway.days')}</div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-ta-green">{timeLeft.hours}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('giveaway.hours')}</div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-ta-green">{timeLeft.minutes}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('giveaway.minutes')}</div>
                </div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-ta-green">{timeLeft.seconds}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('giveaway.seconds')}</div>
                </div>
              </div>
            </div>
            
            {/* Entry Form */}
            <div id="giveaway-form" className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                📧 {t('giveaway.enterGiveaway')}
              </h3>
              
              {isSubmitted ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-lg p-4 text-center">
                  <h4 className="text-lg font-medium text-green-800 dark:text-green-300 mb-2">
                    {t('giveaway.entryReceived')}
                  </h4>
                  <p className="text-green-700 dark:text-green-400">
                    {t('giveaway.thankYou')}
                  </p>
                </div>
              ) : (
                <form 
                  name="giveaway-entry" 
                  method="POST" 
                  data-netlify="true"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <input type="hidden" name="form-name" value="giveaway-entry" />
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('giveaway.yourEmail')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder={t('giveaway.enterEmail')}
                        className="pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-ta-green focus:border-ta-green dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors shadow-md hover:shadow-lg flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Enter Giveaway
                        <ChevronRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <GiveawayTermsModal 
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </section>
  );
};

export default GiveawaySection;